"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";

import { api } from "@/shared/lib/api";
import { extractApiErrorMessage } from "@/shared/api/error-handler";
import { roleFromApi, roleToApi } from "@/shared/lib/lol-roles";
import { Button } from "@/shared/ui/buttons/button";

import {
  RegistrationRoleFields,
  type RegistrationRoleFieldsValue,
} from "./registration-role-fields";

/** Часть ответа `GET /tournaments/{id}/my-registration`, из которой заполняется форма. */
export interface EditableRegistration {
  mainRole?: string | null;
  secondaryRole?: string | null;
  desiredRole?: string | null;
  playerNote?: string | null;
}

interface Props {
  tournamentId: string;
  registration: EditableRegistration;
  /** Капитанство и заметка показываются только в драфте. */
  isDraft: boolean;
  /** Строка со статусом заявки — рендерится над полями. */
  statusSlot?: React.ReactNode;
  onSaved: (updated: unknown) => void;
  onCancel: () => void;
}

/**
 * Правка уже поданной заявки.
 *
 * Отличается от регистрации не набором полей, а тем, чего в ней НЕТ: Riot-аккаунт,
 * выбор команды и кастомные поля бэкенд при правке не принимает
 * (`MyRegistrationUpdateRequest` — только роли, капитанство и заметка).
 */
export function TournamentRegistrationEditForm({
  tournamentId,
  registration,
  isDraft,
  statusSlot,
  onSaved,
  onCancel,
}: Props) {
  const t = useTranslations("Tournaments");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Значения снимаем один раз при монтировании: фоновый рефетч статуса иначе затирал бы
  // то, что человек уже успел поменять в открытой форме.
  const [value, setValue] = useState<RegistrationRoleFieldsValue>(() => ({
    mainRole: roleFromApi(registration.mainRole),
    secondaryRole: roleFromApi(registration.secondaryRole),
    wantsCaptain: (registration.desiredRole ?? "") === "Captain",
    playerNote: registration.playerNote ?? "",
  }));

  const patch = (p: Partial<RegistrationRoleFieldsValue>) =>
    setValue((prev) => ({ ...prev, ...p }));

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/tournaments/${tournamentId}/my-registration`,
        {
          // Токен «Любая» разворачивается в null: PATCH разбирает роль через
          // Enum.TryParse и на неизвестном значении отвечает 400 (в отличие от
          // создания заявки, где нераспознанная роль молча игнорируется).
          mainRole: roleToApi(value.mainRole) ?? null,
          // Отправляем ВСЕГДА. UpdateMyRegistrationAsync присваивает SecondaryRole
          // безусловно, поэтому неотправленное поле означает «стереть»: пока эта
          // форма её не слала, правка заявки молча теряла дополнительную роль.
          secondaryRole: roleToApi(value.secondaryRole) ?? null,
          desiredRole: value.wantsCaptain ? "Captain" : "Participant",
          playerNote: value.playerNote.trim() || null,
        },
      );
      onSaved(data);
    } catch (err) {
      setError(extractApiErrorMessage(err, t("actions.myRegistrationError")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 py-2">
      {statusSlot}

      <RegistrationRoleFields
        value={value}
        onChange={patch}
        showCaptainAndNote={isDraft}
        idPrefix="edit-reg"
      />

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={saving}>
          {t("actions.cancelRegistrationKeep")}
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? t("actions.saving") : t("actions.save")}
        </Button>
      </div>
    </div>
  );
}
